import { MouseEvent, useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import TagManager from 'react-gtm-module';
import { FormattedMessage, useIntl } from 'react-intl';
import { Container, Grid, Button, AppBar, Toolbar, Box, Typography } from '@mui/material';
import { Person } from '@mui/icons-material';
import GolferMenu from './GolferMenu';
import { HtmlTooltip } from './HtmlTooltip';
import * as GolferHelper from '../library/GolferHelper';
import * as FitterHelper from '../library/FitterHelper';
import * as TutorialHelper from '../library/TutorialHelper';
import * as SimulatorHelper from '../library/SimulatorHelper';
import { logDev } from '../library/Globals';
import * as TrackMan from '../classes/TrackMan';
import Golfer, { PreferenceHand, PreferenceHandicap, PreferenceConditions, PreferenceConfidence } from '../classes/Golfer';

export default function GolferPreferences() {
    const [golfer, setGolfer] = useState(new Golfer());
    const [pageIsValid, setPageIsValid] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [showTutorial, setShowTutorial] = useState(false);
    const [ws, setWs] = useState({} as WebSocket);
    const [isConnected, setIsConnected] = useState(false);
    const [showIsConnectedMessage, setShowIsConnectedMessage] = useState(false);

    const intl = useIntl();
    const intlAnswers = intl.formatMessage({ id: "golfer.info.answers" });
    const intlIsOnline = intl.formatMessage({ id: "settings.connection.successful" });
    const [intlIsOfflineMsg, setIntlIsOfflineMsg] = useState('');

    useEffect((): void => {
        TagManager.dataLayer({
            dataLayer: {
                event: 'vwf_golferpreferences'
            }
        });

        //connect to iOS
        (window as any).iOSMessage = iOSMessage;

        const getAsyncData = async () => {
            setShowTutorial(await TutorialHelper.isActive());
            let newGolfer = await GolferHelper.getCurrentGolfer();
            logDev(`"newGolfer", ${newGolfer}`)
            setGolfer(newGolfer);
            TestTrackManConnection();
        }
        getAsyncData();
    }, []);

    const TestTrackManConnection = async () => {
        let fitter = await FitterHelper.getFitter();
        //send message to iOS if necessary
        if (fitter.iOS) {
            try {
                (window as any).webkit.messageHandlers.iOSConnectTM.postMessage("ios");
            }
            catch { }
        } else {
            //no iOS app, talk to TM directly
            setTimeout(() => {
                connectToTrackMan();
            }, 100);
        }
    }

    const iOSMessage = (msg: string) => {
        try {
            let data = JSON.parse(msg) as TrackMan.Data;
            setIsConnected(data.SubType === "connected");
        }
        catch (err) {
            TagManager.dataLayer({
                dataLayer: {
                    event: 'log_error',
                    description: 'Golfer Preferences - get iOS message',
                    msg: 'catch: ' + err
                }
            });
        }
    }

    const wsClose = () => {
        try {
            ws.close();
        }
        catch { }
    }

    const connectToTrackMan = async () => {
        let socket: WebSocket;
        connectToTrackManTimer();
        try {
            let newSimulatorConfiguration = await SimulatorHelper.getConfiguration();
            let simulator = await SimulatorHelper.getSimulator(newSimulatorConfiguration);
            let url = simulator.getUrl(newSimulatorConfiguration);

            //change name of error message from TrackMan to GCQuad if necessary
            var isOffline = intl.formatMessage({ id: "settings.connection.failed" });
            if (simulator.modelType.toLowerCase() === "foresight") {
                isOffline = isOffline.replace("TrackMan Finder", "GCQuad");
            }
            setIntlIsOfflineMsg(isOffline);

            socket = new WebSocket(url);
            setWs(socket);

            //if the device is online, there will be a confirmation response within one second. Otherwise it might take ten seconds to see the message
            setTimeout(() => {
                setShowIsConnectedMessage(true);
            }, 1000)

            socket.onopen = async function (e) {
                if (simulator.modelType === "trackman"){
                    setConnectionStatus(true);
                }

                //after two seconds (an abitrary number), close the websocket
                setTimeout(() => {
                    wsClose();
                }, 2000);
            };

            socket.onmessage = function(msg) {
                if (simulator.modelType === "foresight"){
                    logDev(msg.data);
                    if (msg.data){
                      let msgData = msg.data.toLowerCase();
                      if (msgData.includes("status")){
                        let isConnected = (msgData.includes(" connected"));
                        setConnectionStatus(isConnected);
                      }
                    }
                }
            }

            socket.onerror = function (err) {
                setConnectionStatus(false)
                wsClose();
            };
        }
        catch (err) {
            TagManager.dataLayer({
                dataLayer: {
                    event: 'log_error',
                    description: 'Golfer Preferences - connect to TrackMan',
                    msg: 'catch: ' + err
                }
            });
            setIsConnected(false);
            setShowIsConnectedMessage(true);
        }
    }

    const setConnectionStatus = async (status: boolean) => {
        setIsConnected(status);
        setShowIsConnectedMessage(true);
    }

    const connectToTrackManTimer = () => {
        //attempt another connection after a few seconds
        //only do this if we are still on the GolferPreferences page
        if (window.location.href.includes('golferpreferences')) {
            setIsConnected(prevValue => {
                if (!prevValue) {
                    setTimeout(() => {
                        connectToTrackMan();
                    }, 3000);
                }
                return prevValue;
            });
        }
    }

    const handleFormChange = (event: MouseEvent<HTMLButtonElement>): void => {
        switch (event.currentTarget.name) {
            case "courseconditions":
                TagManager.dataLayer({
                    dataLayer: {
                        event: 'vwf_fit_select_course_conditions'
                    }
                })
                break;
            case "bunkerconditions":
                TagManager.dataLayer({
                    dataLayer: {
                        event: 'vwf_fit_select_bunker_conditions'
                    }
                })
                break;
            case "confidience":
                TagManager.dataLayer({
                    dataLayer: {
                        event: 'vwf_fit_select_confidence'
                    }
                })
                break;
        }
        let newGolfer = { ...golfer, [event.currentTarget.name]: event.currentTarget.value } as Golfer;
        validatePage(newGolfer); //refresh error messages if necessary
        setGolfer(newGolfer);
    }

    let navigate = useNavigate();

    const handleSubmit = async (event: MouseEvent<HTMLButtonElement>) => {
        //send gtm about whether there was a connection or not
        if (isConnected) {
            TagManager.dataLayer({
                dataLayer: {
                    event: 'vwf_monitor_connection',
                    q_connection: true
                }
            });
        } else {
            TagManager.dataLayer({
                dataLayer: {
                    event: 'vwf_monitor_connection',
                    method: 'Alert Message',
                    q_connection: false
                }
            });
        }

        //at this point, the vwf_session cookie should have been set by the Google Tag Manager code. Save it here
        let vwfSession = getCookie("vwf_session");
        golfer.vwfSession = vwfSession;

        let isValid = validatePage(golfer);
        setShowErrors(!isValid);
        if (isValid) {
            TagManager.dataLayer({
                dataLayer: {
                    event: 'vwf_golfer_information_next',
                    q_hand: golfer.hand,
                    q_handicap: golfer.handicap,
                    q_conditions_course: golfer.courseConditions,
                    q_conditions_bunker: golfer.bunkerConditions,
                    q_confidence: golfer.confidence
                }
            })
            //need to save the golfer selections in the 'origX' properties so we don't lose these values if they get changed on the Why Grinds page
            golfer.origCourseConditions = golfer.courseConditions;
            golfer.origBunkerConditions = golfer.bunkerConditions;
            golfer.origConfidence = golfer.confidence;
            await GolferHelper.upsertGolfer(golfer);
            navigate('/apps/wedgefitting/inbag');
        }
    }

    const getCookie = (name: string) => {
        var cookieName = "";
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`) || [];
        if (parts !== undefined && parts.length === 2) {
            let partsPop = parts.pop();
            if (partsPop) {
                cookieName = partsPop.split(';').shift() || "";
            }
        }
        return cookieName;
    }

    const handleQuit = () => {
        navigate('/apps/wedgefitting/golferinfo');
    }

    const handleTutorialNext = () => {
        navigate('/apps/wedgefitting/inbag');
    }

    const handleTutorialCancel = async () => {
        await TutorialHelper.disable();
        setShowTutorial(false);
        navigate('/apps/wedgefitting/golferinfo');
    }

    const validatePage = (golfer: Golfer): boolean => {
        let isValid = golfer.hand !== '' as PreferenceHand && golfer.handicap !== '' as PreferenceHandicap && golfer.courseConditions !== '' as PreferenceConditions &&
            golfer.bunkerConditions !== '' as PreferenceConditions && golfer.handicap !== '' as PreferenceHandicap && golfer.confidence !== '' as PreferenceConfidence;

        setPageIsValid(isValid);
        return isValid;
    }

    const cbGolferUpdate = async (cbGolfer: Golfer) => {
        let newGolfer = { ...golfer, firstName: cbGolfer.firstName, lastName: cbGolfer.lastName, fullName: cbGolfer.fullName, email: cbGolfer.email, phone: cbGolfer.phone, smsOptIn: cbGolfer.smsOptIn};
        setGolfer(newGolfer);
        await GolferHelper.upsertGolfer(newGolfer);
    }

    return (
        <>
            <Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'flex' } }} className='navbar'>
                <AppBar position="fixed" className='appbar'>
                    <Container maxWidth="lg">
                        <Toolbar>
                            <Grid container>
                                <Grid item xs={4}>
                                    <Typography className='name' variant="h6" component="div" sx={{ marginTop: '10px', display: { xs: 'none', md: 'flex' } }}>
                                        <Person sx={{ width: '30px', marginRight: '5px', marginTop: '-10px', height: '2em' }}></Person>
                                        <GolferMenu golfer={golfer} cbgolferupdate={cbGolferUpdate}></GolferMenu>
                                    </Typography>

                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="h6" component="div" className='title' sx={{ display: 'flex', justifyContent: 'center', marginTop: '7px' }}>
                                        <FormattedMessage id="golfer.info.title" />
                                    </Typography>
                                </Grid>
                                <Grid item xs={4} container justifyContent="flex-end">
                                    <Button disableRipple type="submit" variant="contained" onClick={handleQuit} className='cancel-button'><FormattedMessage id="general.quit" /></Button>
                                    <Button disableRipple type="submit" variant="contained" onClick={handleSubmit} className='submit-button'><FormattedMessage id="general.next" /></Button>
                                </Grid>
                            </Grid>
                        </Toolbar>
                    </Container>
                </AppBar>
            </Box>

            <Container maxWidth='lg'>
                <HtmlTooltip
                    title={
                        <>
                            <Typography color="inherit"><p><FormattedMessage id="quick.start.golfer.info" /></p>
                                <FormattedMessage id="quick.start.click.next" /></Typography>
                            <br />
                            <div style={{ textAlign: 'right' }}>
                                <Button onClick={handleTutorialCancel} className="cancel-button tutorial"><FormattedMessage id="general.close" /></Button>
                                <Button onClick={handleTutorialNext} className="submit-button tutorial"><FormattedMessage id="general.next" /></Button>
                            </div>
                        </>
                    }
                    open={showTutorial}
                    placement={'top'}
                    arrow={false}
                    maxwidth={500}
                >

                    <Grid container className='row-error' sx={{ marginTop: '75px !important' }}>
                    </Grid>
                </HtmlTooltip>
                <Grid container className='row-error'>
                    <Grid item sm={12} className='form-error-label'>
                        {showIsConnectedMessage && (
                            <>
                                <Typography variant="h6" component="div" className='title' sx={{ display: 'flex', justifyContent: 'center', color: 'white', backgroundColor: `${isConnected ? 'green' : 'red'}`, borderRadius: '5px' }}>
                                    {`${isConnected ? intlIsOnline : intlIsOfflineMsg}`}
                                </Typography>

                            </>
                        )}

                    </Grid>
                </Grid>
                <Grid container className='row-error'>
                    <Grid item sm={12} className='form-error-label'><span>{`${!pageIsValid && showErrors ? intlAnswers : ""}`}</span>
                    </Grid>
                </Grid>
                <div className='golfer-preferences-form'>
                    <Grid container className='form-row'>
                        <Grid item sm={12} className='form-label'><span><FormattedMessage id="golfer.info.hand" /></span></Grid>
                        <Grid item sm={12} className='form-row-buttons'>
                            <Button disableRipple type="submit" name='hand' value='Right' variant="outlined" className={`wide ${golfer.hand === "Right" as PreferenceHand ? "selected" : ""}`} onClick={handleFormChange}><FormattedMessage id="golfer.info.right.hand" /></Button>
                            <Button disableRipple type="submit" name='hand' value='Left' variant="outlined" className={`wide ${golfer.hand === "Left" as PreferenceHand ? "selected" : ""}`} onClick={handleFormChange}><FormattedMessage id="golfer.info.left.hand" /></Button>
                        </Grid>
                    </Grid>
                    <Grid container className='form-row'>
                        <Grid item sm={12} className='form-label'><span><FormattedMessage id="golfer.info.handicap" /></span></Grid>
                        <Grid item sm={12} className='form-row-buttons'>
                            <Button disableRipple type="submit" name='handicap' value='0 or Less' variant="outlined" className={`wide ${golfer.handicap === "0 or Less" as PreferenceHandicap ? "selected" : ""}`} onClick={handleFormChange}><FormattedMessage id="golfer.info.or.less" /></Button>
                            <Button disableRipple type="submit" name='handicap' value='1-5' variant="outlined" className={`wide ${golfer.handicap === "1-5" as PreferenceHandicap ? "selected" : ""}`} onClick={handleFormChange}>1-5</Button>
                            <Button disableRipple type="submit" name='handicap' value='6-10' variant="outlined" className={`wide ${golfer.handicap === "6-10" as PreferenceHandicap ? "selected" : ""}`} onClick={handleFormChange}>6-10</Button>
                            <Button disableRipple type="submit" name='handicap' value='11-15' variant="outlined" className={`wide ${golfer.handicap === "11-15" as PreferenceHandicap ? "selected" : ""}`} onClick={handleFormChange}>11-15</Button>
                            <Button disableRipple type="submit" name='handicap' value='16-20' variant="outlined" className={`wide ${golfer.handicap === "16-20" as PreferenceHandicap ? "selected" : ""}`} onClick={handleFormChange}>16-20</Button>
                            <Button disableRipple type="submit" name='handicap' value='20+' variant="outlined" className={`wide ${golfer.handicap === "20+" as PreferenceHandicap ? "selected" : ""}`} onClick={handleFormChange}>20+</Button>
                        </Grid>
                    </Grid>

                    <Grid container className='form-row'>
                        <Grid item sm={12} className='form-label'><span><FormattedMessage id="golfer.info.course" /></span></Grid>
                        <Grid item sm={12} className='form-row-buttons'>
                            <Button disableRipple type="submit" variant="outlined" name='courseConditions' value='Soft' className={`wide ${golfer.courseConditions === "Soft" as PreferenceConditions ? "selected" : ""}`} onClick={handleFormChange}><FormattedMessage id="golfer.info.soft" /></Button>
                            <Button disableRipple type="submit" variant="outlined" name='courseConditions' value='Slightly Soft' className={`wide ${golfer.courseConditions === "Slightly Soft" as PreferenceConditions ? "selected" : ""}`} onClick={handleFormChange}><FormattedMessage id="golfer.info.slightly.soft" /></Button>
                            <Button disableRipple type="submit" variant="outlined" name='courseConditions' value='Medium' className={`wide ${golfer.courseConditions === "Medium" as PreferenceConditions ? "selected" : ""}`} onClick={handleFormChange}><FormattedMessage id="golfer.info.medium" /></Button>
                            <Button disableRipple type="submit" variant="outlined" name='courseConditions' value='Slightly Firm' className={`wide ${golfer.courseConditions === "Slightly Firm" as PreferenceConditions ? "selected" : ""}`} onClick={handleFormChange}><FormattedMessage id="golfer.info.slightly.firm" /></Button>
                            <Button disableRipple type="submit" variant="outlined" name='courseConditions' value='Firm' className={`wide ${golfer.courseConditions === "Firm" as PreferenceConditions ? "selected" : ""}`} onClick={handleFormChange}><FormattedMessage id="golfer.info.firm" /></Button>
                        </Grid>
                    </Grid>
                    <Grid container className='form-row'>
                        <Grid item sm={12} className='form-label'><span><FormattedMessage id="golfer.info.bunker" /></span></Grid>
                        <Grid item sm={12} className='form-row-buttons'>
                            <Button disableRipple type="submit" variant="outlined" name='bunkerConditions' value='Soft' className={`wide ${golfer.bunkerConditions === "Soft" as PreferenceConditions ? "selected" : ""}`} onClick={handleFormChange}><FormattedMessage id="golfer.info.soft" /></Button>
                            <Button disableRipple type="submit" variant="outlined" name='bunkerConditions' value='Slightly Soft' className={`wide ${golfer.bunkerConditions === "Slightly Soft" as PreferenceConditions ? "selected" : ""}`} onClick={handleFormChange}><FormattedMessage id="golfer.info.slightly.soft" /></Button>
                            <Button disableRipple type="submit" variant="outlined" name='bunkerConditions' value='Medium' className={`wide ${golfer.bunkerConditions === "Medium" as PreferenceConditions ? "selected" : ""}`} onClick={handleFormChange}><FormattedMessage id="golfer.info.medium" /></Button>
                            <Button disableRipple type="submit" variant="outlined" name='bunkerConditions' value='Slightly Firm' className={`wide ${golfer.bunkerConditions === "Slightly Firm" as PreferenceConditions ? "selected" : ""}`} onClick={handleFormChange}><FormattedMessage id="golfer.info.slightly.firm" /></Button>
                            <Button disableRipple type="submit" variant="outlined" name='bunkerConditions' value='Firm' className={`wide ${golfer.bunkerConditions === "Firm" as PreferenceConditions ? "selected" : ""}`} onClick={handleFormChange}><FormattedMessage id="golfer.info.firm" /></Button>
                        </Grid>
                    </Grid>
                    <Grid container className='form-row'>
                        <Grid item sm={12} className='form-label'><span><FormattedMessage id="golfer.info.confidence" /></span></Grid>
                        <Grid item sm={12} className='form-row-buttons'>
                            <Button disableRipple type="submit" name='confidence' value='Low' variant="outlined" className={`wide ${golfer.confidence === "Low" as PreferenceConfidence ? "selected" : ""}`} onClick={handleFormChange}><FormattedMessage id="golfer.info.low" /></Button>
                            <Button disableRipple type="submit" name='confidence' value='Less' variant="outlined" className={`wide ${golfer.confidence === "Less" as PreferenceConfidence ? "selected" : ""}`} onClick={handleFormChange}><FormattedMessage id="golfer.info.less" /></Button>
                            <Button disableRipple type="submit" name='confidence' value='Medium' variant="outlined" className={`wide ${golfer.confidence === "Medium" as PreferenceConfidence ? "selected" : ""}`} onClick={handleFormChange}><FormattedMessage id="golfer.info.medium" /></Button>
                            <Button disableRipple type="submit" name='confidence' value='More' variant="outlined" className={`wide ${golfer.confidence === "More" as PreferenceConfidence ? "selected" : ""}`} onClick={handleFormChange}><FormattedMessage id="golfer.info.more" /></Button>
                            <Button disableRipple type="submit" name='confidence' value='High' variant="outlined" className={`wide ${golfer.confidence === "High" as PreferenceConfidence ? "selected" : ""}`} onClick={handleFormChange}><FormattedMessage id="golfer.info.high" /></Button>
                        </Grid>
                    </Grid>
                </div>
            </Container>
        </>
    )
}